import { __decorate, __metadata, __param } from "tslib";
import _, { flatMap, map, findIndex, join } from 'lodash';
import dayjs from 'dayjs';
import { inject, injectable } from 'inversify';
import { E_ExpiredPrivilegeOptions, E_HitCompanySource } from '@/apis/service/CompanyApi.types';
import { Symbols } from '@/symbols';
import { E_PackageTag } from '@/apis/service/PackageApi.types';
import { E_CooperativeCompanyStatus } from '../../modules/cooperative-company/service/CooperativeCompanyService.type';
import { splitPackages } from '../../utils/Privilege';
import { E_CompanyLegitimateStatus } from '@/constants/companyBase';
import { E_ComponentVariant } from '@/constants/ui';
export * from '@/apis/service/CompanyApi.types';
let CompanyService = class CompanyService {
    constructor(companyApi, xiaoshouyiApi, cooperativeCompanyService, miscApi, systemService, authService, i18n) {
        this.companyApi = companyApi;
        this.xiaoshouyiApi = xiaoshouyiApi;
        this.cooperativeCompanyService = cooperativeCompanyService;
        this.miscApi = miscApi;
        this.systemService = systemService;
        this.authService = authService;
        this.i18n = i18n;
        /**
         * 根据公司名字获取公司信息
         */
        this.getCompanyCrmInfo = this.companyApi.getCompanyCrmInfo;
        this.companyInfo = null;
        /**
         * 同步销售易
         */
        this.syncXiaoshouyi = this.xiaoshouyiApi.syncById;
        /**
         * 同步Salesforce
         */
        this.postSalesforceSynchronize = this.miscApi.postSalesforceSynchronize;
        /** 同步计费类型 */
        this.postChargeModeCompute = this.miscApi.postChargeModeCompute;
        /**
         * 批量编辑公司
         */
        this.postCompaniesBatchEdit = this.companyApi.postCompaniesBatchEdit;
        /**
         * 获取公司上的销售和客服
         */
        this.getSalesCSByCompanyId = this.companyApi.getSalesCSByCompanyId;
        this.companyAttributeData = {
            company_tffi: {},
            company_finance: {},
            company_salesforce: {},
            entity_id: '',
        };
    }
    /**
     * 自动补全公司名称相关
     */
    async querySearchCompany(hitSource, queryString, params) {
        let companyNames = [];
        try {
            if (hitSource === E_HitCompanySource.Cooperated) {
                const paid_type = params?.paid_type;
                const paramsSend = {
                    iDisplayLength: 20,
                    company_name: queryString,
                    status: E_CooperativeCompanyStatus.Active,
                };
                if (paid_type) {
                    paramsSend.paid_type = paid_type;
                }
                const res = await this.cooperativeCompanyService.getCooperativeCompanyList(paramsSend);
                companyNames = _.map(res.aaData, r => {
                    const { company_name, id, } = r;
                    return { value: company_name, id, render_tag: [] };
                });
            }
            else if (hitSource === E_HitCompanySource.Tianyancha) {
                const res = await this.miscApi.getCompanyInfoByTyc({
                    companyName: queryString,
                });
                companyNames = _.map(res, r => {
                    return {
                        value: r,
                        id: '',
                        render_tag: [],
                    };
                });
            }
            else if (hitSource === E_HitCompanySource.Normal) {
                const res = await this.companyApi.getCompaniesByKeyword(queryString, params);
                companyNames = _.map(res.company_info_list, r => {
                    /** 只显示未认证的标签 */
                    const legitimateTag = r.legitimate === 0 ? ['legitimate_0'] : [];
                    let source_tags = [];
                    if (!r.charge_mode) {
                        source_tags = r.source_tags;
                    }
                    const render_tag = _.filter(_.concat([], legitimateTag, (source_tags || [])), (el) => {
                        /** 过滤掉已创建标签 */
                        return el !== 'identity_company';
                    });
                    return {
                        value: r.company_name,
                        id: r.identity_company_id,
                        render_tag: render_tag,
                        charge_mode: r.charge_mode,
                    };
                });
            }
        }
        catch (err) {
            this.systemService.reportError(err);
        }
        return companyNames;
    }
    /** 获取公司信息 */
    getCompanyList(params) {
        const updatedParams = {
            ...params,
            // 限制销售只能显示部分公司
            s_validate_role: 'sales',
            // 显示过期权限改为 boolean
            s_contain_expired_privileges: params.s_contain_expired_privileges === E_ExpiredPrivilegeOptions.YES,
        };
        return this.companyApi.getCompanyList(updatedParams);
    }
    isExpireNowFromNumber(time) {
        const expire = dayjs(time);
        /* 检查一个 moment 是否在另一个 moment 之前
        dayjs('2010-10-20').isBefore('2010-10-21'); // true */
        return expire.isBefore(dayjs());
    }
    companyPrivilegePackagesInfo(company_privileges) {
        const res = flatMap(company_privileges, pv => {
            const { id, packages, privilege_id, register_scope, contracts, credits_enabled, } = pv;
            return flatMap(pv.contract_duration, (contract_duration, index) => {
                const { seat, assigned, begin_at, end_at, concurrency, charge_mode, } = contract_duration;
                // 过期不显示
                if (this.isExpireNowFromNumber(end_at)) {
                    return [];
                }
                return [{
                        seat,
                        assigned,
                        key: pv.id + '-' + index,
                        id,
                        privilege_id,
                        packages,
                        beginAtTime: begin_at,
                        endAtTime: end_at,
                        concurrency,
                        registerScope: register_scope,
                        contracts,
                        charge_mode,
                        credits_enabled,
                    }];
            });
        });
        return res;
    }
    packageInfosFlat(packageInfos) {
        return map(packageInfos, info => {
            const { packages, beginAtTime, endAtTime, registerScope, contracts, } = info;
            let { concurrency, assigned, seat, } = info;
            const { mainPackages, subPackages, } = splitPackages(packages);
            const isAnonymousPrivilege = findIndex(packages, pkg => pkg.package_tag === E_PackageTag.ANONYMOUS) > -1;
            if (isAnonymousPrivilege) {
                assigned = '-';
                seat = '-';
            }
            const getPackageName = (pkg) => {
                if (this.i18n.locale === 'cn') {
                    return pkg.package_name;
                }
                else {
                    return pkg.en_name;
                }
            };
            return {
                main: mainPackages,
                mainShow: join(map(mainPackages, getPackageName), ', '),
                sub: subPackages,
                subShow: join(map(subPackages, getPackageName), ', '),
                assigned,
                beginAtTime,
                endAtTime,
                seat,
                concurrency: concurrency != null ? concurrency : '-',
                registerScope,
                contracts,
            };
        });
    }
    companyPrivilegeAllPackages(company_privileges, is_show_expired = false) {
        const privilegePackages = _.map(company_privileges, pv => {
            const { id, packages, privilege_id, register_scope, contracts, credits_enabled, } = pv;
            const new_contract_duration = _.map(pv.contract_duration, (contract_duration, index) => {
                const { seat, assigned, begin_at, end_at, concurrency, charge_mode, } = contract_duration;
                return {
                    is_expired: this.isExpireNowFromNumber(end_at),
                    seat,
                    assigned,
                    key: pv.id + '-' + index,
                    id,
                    privilege_id,
                    packages,
                    beginAtTime: begin_at,
                    endAtTime: end_at,
                    concurrency,
                    registerScope: register_scope,
                    contracts,
                    charge_mode,
                };
            });
            const { mainPackages, subPackages, } = splitPackages(packages);
            const getPackageName = (pkg) => {
                if (this.i18n.locale === 'cn') {
                    return pkg.package_name;
                }
                else {
                    return pkg.en_name;
                }
            };
            const final_contract_duration = !is_show_expired ? _.filter(new_contract_duration, el => !el.is_expired) : new_contract_duration;
            /** 此处的 1 2 是排序的权重 */
            return {
                is_expired: _.every(final_contract_duration, el => el.is_expired),
                contract_duration: _.sortBy(final_contract_duration, el => el.is_expired ? 2 : 1),
                main: mainPackages,
                mainShow: _.join(_.map(mainPackages, getPackageName), ', '),
                sub: subPackages,
                subShow: _.join(_.map(subPackages, getPackageName), ', '),
                contracts,
                credits_enabled,
            };
        });
        const finalPrivilegePackages = !is_show_expired ? _.filter(privilegePackages, el => !el.is_expired) : privilegePackages;
        /** 此处的 1 2 是排序的权重 */
        return _.sortBy(finalPrivilegePackages, el => el.is_expired ? 2 : 1);
    }
    /**
     * 是否可查看公司信息
     * 销售只能查看自己名下的公司
     */
    getCanViewCompany(item) {
        if (!this.authService.IsOnlyViewFlag) {
            return true;
        }
        if (item.belong) {
            return true;
        }
        return false;
    }
    /**
     * 获取特定公司信息
     */
    getCompanyInfo(company_id, includePrivileges = false) {
        return this.companyApi.getCompanyInfo(company_id, includePrivileges).then(res => {
            this.companyInfo = res;
            return res;
        });
    }
    postCompanyInvitation(data) {
        return this.companyApi.postCompanyInvitation(data);
    }
    getCompanyIsIllegitimate(companyInfo) {
        return companyInfo.legitimate === E_CompanyLegitimateStatus.illegal;
    }
    isCompanyNameIllegal(companyInfo) {
        return companyInfo.legitimate === E_CompanyLegitimateStatus.illegal;
    }
    getCompanyLegitimateConfig(companyInfo) {
        let tip = '';
        let label = '';
        let variant = '';
        if (companyInfo.legitimate === E_CompanyLegitimateStatus.illegal) {
            tip = this.i18n.t('backoffice.company.companyNameNotCertified');
            label = this.i18n.t('backoffice.company_tags.legitimate_0');
            variant = E_ComponentVariant.Secondary;
        }
        if (companyInfo.legitimate === E_CompanyLegitimateStatus.legal) {
            tip = this.i18n.t('backoffice.company.companyNameCertified');
            label = this.i18n.t('backoffice.company_tags.legitimate_1');
            variant = E_ComponentVariant.Primary;
        }
        return {
            tip,
            label,
            variant,
        };
    }
    getCustomerViewCompanyAttribute(companyId) {
        return this.companyApi.getCustomerViewCompanyAttribute(companyId).then(res => {
            this.companyAttributeData = {
                company_tffi: res.company_tffi || {},
                company_finance: res.company_finance || {},
                company_salesforce: res.company_salesforce || {},
                entity_id: res.entity_id || '',
            };
            return res;
        });
    }
    getCompanyTagData(params) {
        return this.companyApi.getCompanyTagPagination(params);
    }
};
CompanyService = __decorate([
    injectable(),
    __param(0, inject(Symbols.CompanyApi)),
    __param(1, inject(Symbols.CompanyXiaoshouyiApi)),
    __param(2, inject(Symbol.for('CooperativeCompanyService'))),
    __param(3, inject(Symbols.MiscApi)),
    __param(4, inject(Symbols.SystemService)),
    __param(5, inject(Symbols.AuthService)),
    __param(6, inject(Symbols.I18n)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object, Object, Object])
], CompanyService);
export { CompanyService };
